import { ButtonSize, ButtonVariants } from '@anm/components/buttons/Button';
import { InputVariants } from '@anm/components/form/Input';
import noop from '@anm/helpers/noop';
import { useCallback, FC, MouseEvent, ChangeEvent, KeyboardEvent } from 'react';

import Field from './Field';
import FieldWithButtonWrapper, { ButtonWrapper } from './Wrapper';

export type FieldWithButtonDirection = 'horizontal' | 'vertical';

export type FieldWithButtonOptionalProps = {
  error?: string;
  autoFocus?: boolean;
  direction?: FieldWithButtonDirection;
  autoResize?: boolean;
  buttonSize?: ButtonSize;
  placeholder?: string;
  inputVariant?: InputVariants;
  buttonVariant?: ButtonVariants;
  isButtonDisabled?: boolean;
  isFieldClickable?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export type FieldWithButtonProps = FieldWithButtonOptionalProps & {
  value: string;
  disabled: boolean;
  multirows: boolean;
  className?: string;
  onClick?: () => void;
};

export type FieldTypes = HTMLInputElement & HTMLTextAreaElement;

const selectTextInInput = ({ currentTarget }: MouseEvent<FieldTypes>) => {
  const field = currentTarget.getElementsByTagName('input')[0] || currentTarget.getElementsByTagName('textarea')[0];

  field.select();
};

const FieldWithButton: FC<FieldWithButtonProps> = ({
  error,
  value,
  onClick,
  onChange,
  onKeyDown,
  children,
  disabled,
  multirows,
  className,
  autoResize,
  autoFocus,
  placeholder,
  isButtonDisabled = false,
  isFieldClickable = false,
  direction = 'horizontal',
  buttonSize = 'large',
  inputVariant = 'with_button_horizontal',
  buttonVariant = 'light-blue-lowercase-text-field'
}) => {
  const handleButtonClick = useCallback(
    (e: MouseEvent<FieldTypes>) => (!isFieldClickable ? selectTextInInput(e) : noop),
    []
  );

  return (
    <FieldWithButtonWrapper hasError={!!error} className={className} onClick={handleButtonClick} direction={direction}>
      <div>
        <Field
          readOnly={!onChange}
          variant={inputVariant}
          defaultValue={value}
          {...{ autoResize, placeholder, autoFocus, disabled, multirows, isFieldClickable, onChange, onKeyDown }}
        />
        {error && <span>{error}</span>}
      </div>

      <ButtonWrapper
        size={buttonSize}
        onClick={onClick}
        variant={buttonVariant}
        disabled={isButtonDisabled || disabled}
      >
        {children}
      </ButtonWrapper>
    </FieldWithButtonWrapper>
  );
};

export default FieldWithButton;
